import { useState, type FormEvent } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import HoneypotField from '../components/HoneypotField'
import LangLink from '../components/LangLink'
import Section from '../components/Section'
import { sendMessage } from '../api/contact'
import { isRateLimited } from '../api/errors'

const MAP_SRC =
  'https://www.google.com/maps?q=Wafelstraat%2035%2C%208540%20Deerlijk%2C%20Belgium&output=embed'

function ContactPage() {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('')
  const [status, setStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error' | 'rate-limited'
  >('idle')
  const [mountedAt] = useState(() => Date.now())
  // The Google Maps iframe is only put in the DOM after an explicit click, so
  // no request to google.com is made (and no Google cookie is set) until then.
  const [mapShown, setMapShown] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setStatus('sending')
    try {
      await sendMessage({
        name,
        email,
        message,
        website,
        elapsed_ms: Date.now() - mountedAt,
      })
      setStatus('sent')
    } catch (err) {
      setStatus(isRateLimited(err) ? 'rate-limited' : 'error')
    }
  }

  const submitted = status === 'sent'

  return (
    <div className="contact-page">
      {/* ---------- header ---------- */}
      <Section background="surface" space="md" className="contact-hero">
        <p className="eyebrow">{t('contact.eyebrow')}</p>
        <h1 className="display">{t('contact.title')}</h1>
        <p className="contact-lead">
          <Trans
            i18nKey="contact.intro"
            components={{ lnk: <LangLink to="/how-to-order" /> }}
          />
        </p>
      </Section>

      {/* ---------- company details: bordeaux "at a glance" band ----------
          Bordeaux here (not the shared dark variant the other pages use) so
          it does not read as a second copy of the dark footer just below. */}
      <Section background="bordeaux" space="lg" className="contact-details">
        <h2>{t('contact.companyHeading')}</h2>
        <dl className="spec-list contact-spec">
          <div>
            <dt>{t('contact.details.address')}</dt>
            <dd>{t('contact.details.addressValue')}</dd>
          </div>
          <div>
            <dt>{t('contact.details.email')}</dt>
            <dd>
              <a href={`mailto:${t('contact.details.emailValue')}`}>
                {t('contact.details.emailValue')}
              </a>
            </dd>
          </div>
          <div>
            <dt>{t('contact.details.phone')}</dt>
            <dd>
              <a
                href={`tel:${t('contact.details.phoneValue').replace(/\s/g, '')}`}
              >
                {t('contact.details.phoneValue')}
              </a>
            </dd>
          </div>
          <div>
            <dt>{t('contact.details.vat')}</dt>
            <dd>{t('contact.details.vatValue')}</dd>
          </div>
        </dl>
      </Section>

      {/* ---------- message form + map ---------- */}
      <Section background="beige" space="lg" className="contact-main">
        <div className="contact-grid">
          <div className="contact-form-col">
            <h2>{t('contact.formHeading')}</h2>
            {submitted ? (
              <p className="contact-sent">{t('contact.sentMessage')}</p>
            ) : (
              <form onSubmit={handleSubmit} className="form">
                <label>
                  {t('contact.form.name')}
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </label>
                <label>
                  {t('contact.form.email')}
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </label>
                <label>
                  {t('contact.form.message')}
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    required
                  />
                </label>
                <HoneypotField value={website} onChange={setWebsite} />
                {status === 'error' && (
                  <p className="error">{t('contact.form.error')}</p>
                )}
                {status === 'rate-limited' && (
                  <p className="error">{t('contact.form.rateLimited')}</p>
                )}
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={status === 'sending'}
                >
                  {status === 'sending'
                    ? t('contact.form.submitting')
                    : t('contact.form.submit')}
                </button>
              </form>
            )}
          </div>

          <div className="contact-map-col">
            <h2>{t('contact.mapHeading')}</h2>
            <div className="contact-map">
              {mapShown ? (
                <iframe
                  title={t('contact.mapTitle')}
                  src={MAP_SRC}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <div className="contact-map-placeholder">
                  <svg
                    className="contact-map-pin"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M12 2c-3.9 0-7 3.1-7 7 0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
                  </svg>
                  <p className="contact-map-address">
                    {t('contact.details.addressValue')}
                  </p>
                  <p className="contact-map-notice">{t('contact.mapNotice')}</p>
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => setMapShown(true)}
                  >
                    {t('contact.mapShow')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* ---------- quote CTA band ---------- */}
      <Section background="bordeaux" space="md" className="contact-cta">
        <h2>{t('home.quote.heading')}</h2>
        <p>{t('home.quote.body')}</p>
        <LangLink to="/how-to-order" className="btn btn--on-dark">
          {t('home.cta')}
        </LangLink>
      </Section>
    </div>
  )
}

export default ContactPage
